import React from 'react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PRIORITY_LABELS = {
  nominated: { label: 'Nominated by absent teacher', desc: 'Honour the teacher\'s pick for the period' },
  same_subject: { label: 'Same subject', desc: 'Teacher who teaches the same subject' },
  same_level: { label: 'Same class level', desc: 'Teacher whose classes are in the same level (Upper / Lower)' },
  low_workload: { label: 'Lower relief workload this week', desc: 'Spread the load across staff' },
  most_available: { label: 'Most free periods on the day', desc: 'Teacher with the most headroom' },
};

const DEFAULT_ORDER = ['nominated', 'same_subject', 'same_level', 'low_workload', 'most_available'];

const SortablePriorityRow = ({ id, index, meta }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    // iOS Safari: these three kill the press-and-hold text-selection callout
    touchAction: 'none',
    WebkitUserSelect: 'none',
    WebkitTouchCallout: 'none',
    userSelect: 'none',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-3 p-2.5 bg-slate-50 border rounded-md cursor-grab active:cursor-grabbing select-none touch-none ${isDragging ? 'border-blue-400 shadow-lg bg-white' : 'border-slate-200'}`}
      data-testid={`relief-priority-item-${id}`}
      aria-roledescription="Draggable priority row"
      aria-label={`${meta.label}, priority ${index + 1}. Press and drag to reorder.`}
    >
      <span
        className="text-slate-400"
        data-testid={`relief-priority-handle-${id}`}
        aria-hidden="true"
      >
        <GripVertical className="h-5 w-5" />
      </span>
      <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-blue-900 text-white text-xs font-semibold">
        {index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">{meta.label}</p>
        <p className="text-[11px] text-slate-500">{meta.desc}</p>
      </div>
    </div>
  );
};

/**
 * Drag-and-drop reorderable relief assignment priorities + a toggle that
 * strictly enforces same-level matching. The sorted array is mirrored into
 * `config.relief_priority_order` via `setConfig`, then persisted on Save.
 */
export const ReliefPrioritySection = ({ config, setConfig }) => {
  const order = (config?.relief_priority_order && config.relief_priority_order.length > 0)
    ? config.relief_priority_order
    : DEFAULT_ORDER;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Dedicated touch sensor so mobile drag works reliably. The short delay
    // prevents native vertical scrolling from fighting with drag initiation.
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.indexOf(active.id);
    const newIndex = order.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setConfig({ ...config, relief_priority_order: arrayMove(order, oldIndex, newIndex) });
  };

  return (
    <Card className="dashboard-card p-6 mt-4" data-testid="relief-priority-card">
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Relief Assignment Priorities</h3>
          <p className="text-sm text-slate-500 mt-1">
            Drag rows to reorder how the auto-assign engine ranks relief teachers. Top = checked first.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <Switch
            checked={!!config?.relief_enforce_same_level}
            onCheckedChange={(v) => setConfig({ ...config, relief_enforce_same_level: v })}
            data-testid="relief-enforce-same-level-toggle"
          />
          <span>Strictly require same-level relief</span>
        </label>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <div className="space-y-2" data-testid="relief-priority-list">
            {order.map((key, idx) => (
              <SortablePriorityRow
                key={key}
                id={key}
                index={idx}
                meta={PRIORITY_LABELS[key] || { label: key, desc: '' }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <p className="mt-3 text-[11px] text-slate-400">
        Save configuration to apply. Changes affect future auto-assign runs; existing assignments are unchanged.
      </p>
    </Card>
  );
};
